import { create } from 'zustand';
import { logChannelRequestFailure } from 'setup/logger/logChannelRequestFailure';
import { analyticsClient } from 'shared/domain/analytics/analyticsClient';
import { issueClient } from 'shared/domain/issue/issueClient';
import { debounce } from 'shared/utils/debounce';
import { createSelectors, StoreName, withDevtools } from 'store/config';
import { createLatestRead } from 'store/latestRead';
import type { FinancialDataState, FinancialDataStore } from './types';

/** Issue reads come in bursts (sync, then changes); one calculation follows them. */
const RELOAD_DEBOUNCE_MS = 500;

const createInitialState = (projectId?: string): FinancialDataState => ({
  projectId,
  financialData: undefined,
  loading: true,
  receivedAt: 0,
});

const useFinancialDataStoreBase = create<FinancialDataStore>()(
  withDevtools((set, get) => {
    const latestRead = createLatestRead();
    const debouncedReload = debounce(() => void get().actions.reload(), RELOAD_DEBOUNCE_MS);

    const setData = (changes: Partial<FinancialDataState>): void =>
      set((state) => ({ data: { ...state.data, ...changes } }));

    return {
      data: createInitialState(),
      actions: {
        open: (projectId) => {
          if (get().data.projectId !== projectId) {
            latestRead.cancel();
            debouncedReload.cancel();
            set({ data: createInitialState(projectId) });
          }
          return get().actions.reload();
        },

        reload: () =>
          latestRead.run(() => analyticsClient.getFinancialData(), {
            onResult: (financialData) => setData({ financialData, loading: false, receivedAt: Date.now() }),
            onError: (error) => {
              logChannelRequestFailure('Could not read financial data', error);
              setData({ loading: false });
            },
          }),

        requestReload: () => debouncedReload(),

        subscribeToChannelMessages: () => {
          const unsubscribe = issueClient.onEvent((event) => {
            if (issueClient.readCollection(event)) {
              get().actions.requestReload();
            }
          });
          return (): void => {
            debouncedReload.cancel();
            unsubscribe();
          };
        },

        reset: () => {
          latestRead.cancel();
          debouncedReload.cancel();
          set({ data: createInitialState() });
        },
      },
    };
  }, StoreName.FinancialData)
);

export const useFinancialDataStore = createSelectors(useFinancialDataStoreBase);

export const getFinancialDataStoreState = (): FinancialDataStore => useFinancialDataStore.getState();
